import { IAppEnum } from '../enum/app.enum';
import { QueryErrorMessages } from '../enum/cube-error-message.enum';
import { TopAppsReportMapper } from '../mappers/top-apps-report.mapper';
import {
  AppTimeSeriesCubeMetric,
  CubeTimeDimensionGranularity,
} from '../enum/cube-schema.enum';
import { ICubeRepository } from '../interface/cube-repository.interface';
import { ITopAppsReportRepository } from '../interface/top-apps-report-repository.interface';
import {
  IWorkingDaysAnalyticsActivityEnum,
  IWorkingDaysAnalyticsActivityMetricsEnum,
} from '../enum/working-days-analytics-activity.enum';
import { CumulativeAverageMapper } from '../mappers/cumulative-avg-duration.mapper';
import { mapAppTimeSeriesCube } from '../mappers/app-time-series.mapper';
import { AppMetricsMapper } from '../mappers/app-metrics.mapper';
import { getDayOfWeek, getFirstMondayString } from '../helpers/datetime.helper';
import { QueryTopAppsReportRequestParamsDto } from '../dto/top-apps/request/query-top-apps-report-request.dto';
import { QueryCumulativeAverageDurationsRequestParamsDto } from '../dto/top-apps/request/query-cumulative-average-durations-request.dto';
import { TopAppsReportResult } from '../dto/top-apps/cube/mapped/top-apps-report-result.dto';
import { CumulativeAverageDurationResult } from '../dto/top-apps/cube/mapped/cumulative-avg-duration-result.dto';
import { CubeConfigDto } from '../dto/common/request/cube-config.dto';
import { AppMetricsResult } from '../dto/top-apps/cube/mapped/app-metrics-result.dto';
import { AppTimeSeriesResult } from '../dto/top-apps/cube/mapped/app-time-series-result.dto';
import { QueryWeekdayCumulativeAverageRequestParamsDto } from '../dto/top-apps/request/query-weekday-cumulative-average-request.dto';
import { QueryAppMetricsRequestParamsDto } from '../dto/top-apps/request/query-app-metrics-request.dto';
import { QueryUserMaxAppMetricsRequestParamsDto } from '../dto/top-apps/request/query-user-max-app-metrics-request.dto';
import { QueryAppTimeSeriesRequestParamsDto } from '../dto/top-apps/request/query-app-time-series-request.dto';

export class TopAppsReportRepository implements ITopAppsReportRepository {
  cubeRepository: ICubeRepository;
  constructor(deps: { repository: ICubeRepository }) {
    this.cubeRepository = deps.repository;
  }

  async queryTopAppsReport(
    params: QueryTopAppsReportRequestParamsDto,
    config: CubeConfigDto
  ): Promise<TopAppsReportResult[]> {
    try {
      const response = await this.cubeRepository.query(config.token, {
        limit: 5,
        timeDimensions: [
          {
            dimension: IWorkingDaysAnalyticsActivityEnum.LocalStartTime,
            granularity: params.granularity
              ? params.granularity
              : CubeTimeDimensionGranularity.Day,
            dateRange: [params.startDate, params.endDate],
          },
        ],
        order: [
          [IWorkingDaysAnalyticsActivityEnum.TotalDuration, 'desc'],
          [IAppEnum.Name, 'desc'],
        ],
        dimensions: [
          IAppEnum.Name,
          IWorkingDaysAnalyticsActivityEnum.AppId,
          IAppEnum.LogoUrl,
          IAppEnum.DisplayColor,
        ],
        measures: [IWorkingDaysAnalyticsActivityEnum.TotalDuration],
      });
      return response.map((row) =>
        TopAppsReportMapper.persistenceToDomain(row)
      );
    } catch (error) {
      throw new Error(
        `${QueryErrorMessages.QueryTopAppsReportError}: ${error}`
      );
    }
  }

  async queryCumulativeAvgDurations(
    params: QueryCumulativeAverageDurationsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<CumulativeAverageDurationResult[]> {
    try {
      let endDate = new Date(params.endTime);
      endDate.setDate(endDate.getDate() + 1);
      const nextDate = endDate.toISOString().split('T')[0];

      const response = await this.cubeRepository.query(config.token, {
        limit: 5,
        filters: [
          {
            member: IWorkingDaysAnalyticsActivityEnum.LocalStartTime,
            operator: 'beforeDate',
            values: [nextDate],
          },
          {
            member: IWorkingDaysAnalyticsActivityEnum.AppId,
            operator: 'equals',
            values: params.appIds,
          },
        ],
        measures: [
          IWorkingDaysAnalyticsActivityEnum.TotalDuration,
          IWorkingDaysAnalyticsActivityEnum.count,
          IWorkingDaysAnalyticsActivityEnum.DayCount,
          IWorkingDaysAnalyticsActivityEnum.WeekCount,
        ],
        dimensions: [IWorkingDaysAnalyticsActivityEnum.AppId],
        order: [[IWorkingDaysAnalyticsActivityEnum.TotalDuration, 'desc']],
      });
      return response.length
        ? response.map((row) =>
            CumulativeAverageMapper.persistenceToDomain(row)
          )
        : [
            {
              appId: params.appIds[0],
              totalDuration: 0,
              count: 0,
              totalCallDuration: 0,
              callCount: 0,
              dayCount: 1,
              weekCount: 1,
            },
          ];
    } catch (error) {
      throw new Error(
        `${QueryErrorMessages.QueryTopAppsReportError}: ${error}`
      );
    }
  }

  async queryWeekdayCumulativeAvgs(
    params: QueryWeekdayCumulativeAverageRequestParamsDto,
    config: CubeConfigDto
  ): Promise<CumulativeAverageDurationResult[]> {
    try {
      let endDate = new Date(params.endTime);
      endDate.setDate(endDate.getDate() + 1);
      const nextDate = endDate.toISOString().split('T')[0];

      const weekday = new Date(params.endTime);
      const currentWeekdayString = getDayOfWeek(weekday.getUTCDay());

      const response = await this.cubeRepository.query(config.token, {
        filters: [
          {
            member: IWorkingDaysAnalyticsActivityEnum.LocalStartTime,
            operator: 'beforeDate',
            values: [nextDate],
          },
          {
            member: IWorkingDaysAnalyticsActivityEnum.AppId,
            operator: 'equals',
            values: params.appIds,
          },
        ],
        measures: [
          IWorkingDaysAnalyticsActivityEnum.TotalDuration,
          IWorkingDaysAnalyticsActivityEnum.count,
          IWorkingDaysAnalyticsActivityEnum.DayCount,
          IWorkingDaysAnalyticsActivityEnum.WeekCount,
        ],
        dimensions: [IWorkingDaysAnalyticsActivityEnum.AppId],
        segments: [
          `WorkingDaysAnalyticsActivity.${
            params.weekday || currentWeekdayString
          }`,
        ],
        order: [[IWorkingDaysAnalyticsActivityEnum.TotalDuration, 'desc']],
      });
      return response.length
        ? response.map((row) =>
            CumulativeAverageMapper.persistenceToDomain(row)
          )
        : [
            {
              appId: params.appIds[0],
              totalDuration: 0,
              count: 0,
              totalCallDuration: 0,
              callCount: 0,
              dayCount: 1,
              weekCount: 1,
            },
          ];
    } catch (error) {
      throw new Error(
        `${QueryErrorMessages.QueryTopAppsReportError}: ${error}`
      );
    }
  }

  async queryAppMetrics(
    params: QueryAppMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<AppMetricsResult[]> {
    try {
      let startDate;
      if (!params.startTime) {
        if (
          params.granularity &&
          params.granularity === CubeTimeDimensionGranularity.Week
        ) {
          startDate = getFirstMondayString(params.endTime);
        } else {
          startDate = params.endTime;
        }
      } else {
        startDate = params.startTime;
      }
      const response = await this.cubeRepository.query(config.token, {
        filters: [
          {
            member: IWorkingDaysAnalyticsActivityEnum.AppId,
            operator: 'equals',
            values: params.appIds,
          },
        ],
        timeDimensions: [
          {
            dimension: IWorkingDaysAnalyticsActivityEnum.LocalStartTime,
            granularity: params.granularity
              ? params.granularity
              : CubeTimeDimensionGranularity.Day,
            dateRange: [startDate, params.endTime],
          },
        ],
        measures: [
          IWorkingDaysAnalyticsActivityEnum.TotalDuration,
          IWorkingDaysAnalyticsActivityEnum.count,
        ],
      });
      return response.length
        ? response.map((row) => {
            return AppMetricsMapper.persistenceToDomain(row);
          })
        : [
            {
              duration: 0,
              count: 0,
              durationPerCount: 0,
              callDuration: 0,
              callCount: 0,
              callDurationPerCount: 0,
            },
          ];
    } catch (error) {
      throw new Error(
        `${QueryErrorMessages.QueryTopAppsReportError}: ${error}`
      );
    }
  }

  async queryUserMaxAppMetrics(
    params: QueryUserMaxAppMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<AppMetricsResult> {
    try {
      let allTimeMaxScores = {
        'WorkingDaysAnalyticsActivity.totalDuration': '',
        'WorkingDaysAnalyticsActivity.count': '',
        'WorkingDaysAnalyticsActivity.averageDurationPerCount': '',
      };
      for (const metric in IWorkingDaysAnalyticsActivityMetricsEnum) {
        const response = await this.cubeRepository.query(config.token, {
          measures: [IWorkingDaysAnalyticsActivityMetricsEnum[metric]],
          filters: [
            {
              member: IWorkingDaysAnalyticsActivityEnum.AppId,
              operator: 'equals',
              values: params.appIds,
            },
          ],
          timeDimensions: [
            {
              dimension: IWorkingDaysAnalyticsActivityEnum.LocalStartTime,
              granularity: params.granularity
                ? params.granularity
                : CubeTimeDimensionGranularity.Day,
            },
          ],
          limit: 1,
          order: [[IWorkingDaysAnalyticsActivityMetricsEnum[metric], 'desc']],
        });
        if (!response.length) {
          continue;
        }
        allTimeMaxScores[IWorkingDaysAnalyticsActivityMetricsEnum[metric]] =
          response[0][IWorkingDaysAnalyticsActivityMetricsEnum[metric]];
      }
      return AppMetricsMapper.persistenceToDomain(allTimeMaxScores);
    } catch (error) {
      throw new Error(`${QueryErrorMessages.QueryUserMaxScoreError}: ${error}`);
    }
  }

  async queryAppTimeSeries(
    params: QueryAppTimeSeriesRequestParamsDto,
    config: CubeConfigDto
  ): Promise<AppTimeSeriesResult[]> {
    try {
      let response, measures;
      if (params.metric === AppTimeSeriesCubeMetric.Interactions) {
        measures = [IWorkingDaysAnalyticsActivityEnum.count];
      } else if (params.metric === AppTimeSeriesCubeMetric.TimeInApp) {
        measures = [IWorkingDaysAnalyticsActivityEnum.TotalDuration];
      } else {
        measures = [
          IWorkingDaysAnalyticsActivityEnum.TotalDuration,
          IWorkingDaysAnalyticsActivityEnum.count,
        ];
      }

      if (params.weekday) {
        response = await this.cubeRepository.query(config.token, {
          filters: [
            {
              member: IWorkingDaysAnalyticsActivityEnum.AppId,
              operator: 'equals',
              values: [params.appId],
            },
          ],
          timeDimensions: [
            {
              dimension: IWorkingDaysAnalyticsActivityEnum.LocalStartTime,
              granularity: params.granularity
                ? params.granularity
                : CubeTimeDimensionGranularity.Day,
              dateRange: [params.startDate, params.endDate],
            },
          ],
          measures: measures,
          segments: [`WorkingDaysAnalyticsActivity.${params.weekday}`],
        });
      } else {
        response = await this.cubeRepository.query(config.token, {
          filters: [
            {
              member: IWorkingDaysAnalyticsActivityEnum.AppId,
              operator: 'equals',
              values: [params.appId],
            },
          ],
          timeDimensions: [
            {
              dimension: IWorkingDaysAnalyticsActivityEnum.LocalStartTime,
              granularity: params.granularity
                ? params.granularity
                : CubeTimeDimensionGranularity.Day,
              dateRange: [params.startDate, params.endDate],
            },
          ],
          measures: measures,
        });
      }
      return response.map((row) => mapAppTimeSeriesCube(row));
    } catch (error) {
      throw new Error(`${QueryErrorMessages.QueryTimeSeriesError}: ${error}`);
    }
  }
}
